import { createTheme } from '@mui/material/styles';

// RepLogo design tokens — dark "ink" palette with a single periwinkle accent.
export const tokens = {
  ink: '#0E1320',       // page background
  surface: '#161D2D',   // panels, cards
  raised: '#1D2538',    // hover / inset surfaces
  line: '#27314A',      // borders, dividers
  text: '#E7EBF3',
  muted: '#8B95AA',
  accent: '#8C9EFF',
  accentStrong: '#A9B7FF',
  onAccent: '#0E1320',
  success: '#5FD3A0',
  error: '#FF8A8A',
};

export const fonts = {
  display: '"Bricolage Grotesque", "Geist", system-ui, sans-serif',
  body: '"Geist", system-ui, -apple-system, "Segoe UI", sans-serif',
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: tokens.accent, light: tokens.accentStrong, dark: '#6F83F0', contrastText: tokens.onAccent },
    success: { main: tokens.success },
    error: { main: tokens.error },
    background: { default: tokens.ink, paper: tokens.surface },
    text: { primary: tokens.text, secondary: tokens.muted },
    divider: tokens.line,
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: fonts.body,
    h1: { fontFamily: fonts.display, fontWeight: 700, fontSize: 'clamp(2.25rem, 5vw, 3.75rem)', lineHeight: 1.05, letterSpacing: '-0.02em' },
    h2: { fontFamily: fonts.display, fontWeight: 650, fontSize: 'clamp(1.6rem, 3vw, 2.125rem)', lineHeight: 1.15, letterSpacing: '-0.01em' },
    h3: { fontFamily: fonts.display, fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.3 },
    h4: { fontFamily: fonts.display, fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.25 },
    h5: { fontFamily: fonts.display, fontWeight: 600, fontSize: '1.25rem' },
    h6: { fontFamily: fonts.display, fontWeight: 600, fontSize: '1.0625rem' },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.9rem', lineHeight: 1.55 },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: tokens.ink },
        // Keep anchored sections clear of the sticky header
        '[id]': { scrollMarginTop: '80px' },
        '::selection': { background: tokens.accent, color: tokens.onAccent },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': { animationDuration: '0.01ms !important', transitionDuration: '0.01ms !important' },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 20px',
          '&.Mui-focusVisible': { outline: `2px solid ${tokens.accentStrong}`, outlineOffset: 2 },
        },
        sizeLarge: { padding: '14px 26px', fontSize: '1.0625rem' },
        containedPrimary: { '&:hover': { backgroundColor: tokens.accentStrong } },
        outlined: {
          borderColor: tokens.line,
          color: tokens.text,
          '&:hover': { borderColor: tokens.muted, backgroundColor: tokens.raised },
        },
        text: { color: tokens.muted, '&:hover': { color: tokens.text, backgroundColor: tokens.raised } },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none', border: `1px solid ${tokens.line}` },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 14 } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 10 } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.ink,
          '& fieldset': { borderColor: tokens.line },
          '&:hover fieldset': { borderColor: tokens.muted },
        },
      },
    },
  },
});

export default theme;
